import React, { useEffect, useState } from 'react';
import { Modal, Table, Button, Form } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import './BillDetails.scss';
import { FaPlus, FaCalendar, FaTrash, FaPen } from "react-icons/fa";
import { formatCurrency } from '../../../../../../../../../../../../utils/functions';
import { createLocalBill, deleteLocalBillById } from '../../../../../../../../../../../../crud/management_api/local_bills';
import { toast } from 'react-toastify';
import { BillType } from '../../../../../../../../types';
import TablePagination from '../../../../../../../../../../../../components/common/Table/TablePagination';
import Calendar from 'react-calendar';
import ModalConfirmDelete from '../ModalConfirmDelete/ModalConfirmDelete';
import ModalEditBill from '../ModalEditBill/ModalEditBIll';
import ModalEditBillItem from '../ModalEditBillItem/ModalEditBillItem';
import FilesDisplayer from './components/FilesDisplayer';


interface BillDetailsProps {
  billsList: any; // Lista de facturas asociadas
  setBillsList: any; // Setter para actualizar la lista
  show: boolean; // Estado para mostrar u ocultar el modal
  handleClose: () => void; // Función para cerrar el modal
  handleAddBill: (bill: BillType) => void; // Callback para agregar la factura
  isAdminOrSuperAdmin?: boolean; // Prop para verificar si el usuario es admin o superadmin
  handleEditLocalCategory?: (editedCategory: { local_bill_category_id: number; item_name: string; item_description?: string }) => void;
  fetchBills?: () => void; // Función opcional para refrescar la lista global
}

const formatDate = (dateString: string): string => {
  // Si el formato es YYYY-MM-DD, parsear manualmente para evitar desfase de zona horaria
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Mes base 0
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  // Si no, usar el parseo normal
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BillDetails: React.FC<BillDetailsProps> = ({
  billsList,
  setBillsList,
  show,
  handleClose,
  handleAddBill,
  isAdminOrSuperAdmin,
  handleEditLocalCategory,
  fetchBills,
}) => {
  const [newBillName, setNewBillName] = useState('');
  const [newBillAmount, setNewBillAmount] = useState('');
  const [newBillDate, setNewBillDate] = useState('');
  const [newBillNumber, setNewBillNumber] = useState('');
  const [files, setFiles] = useState<File[]>([]); // Para almacenar los archivos
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [billIdToDelete, setBillIdToDelete] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [billToEdit, setBillToEdit] = useState<any | null>(null);
  // Para editar ítem de cuenta (categoría)
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

const handleSubmit = async () => {
  // Asegurarse de que la fecha esté correctamente seteada
  let billDateToSend = newBillDate;
  if (!billDateToSend && selectedDate) {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    billDateToSend = `${year}-${month}-${day}`;
    setNewBillDate(billDateToSend);
  }

  // Validaciones de campos obligatorios
  const faltantes: string[] = [];
  if (!newBillName.trim())   faltantes.push("Nombre");
  if (!newBillAmount.trim()) faltantes.push("Monto");
  if (!selectedDate)         faltantes.push("Fecha");

  if (faltantes.length > 0) {
    const lista =
      faltantes.length === 1
        ? faltantes[0]
        : `${faltantes.slice(0, -1).join(", ")} y ${faltantes.slice(-1)}`;
    alert(`Por favor completa los siguientes campos: ${lista}.`);
    return;
  }

  // Validación de duplicado de número de factura
  const nuevoNum = parseInt(newBillNumber, 10);
  if (billsList.bills?.some((b: any) => b.bill_number === nuevoNum)) {
    toast.error("Ya existe una factura con ese número.");
    return;
  }

  setLoading(true);
  try {
    const user = JSON.parse(localStorage.getItem("user") ?? "{}");
    const localId = user?.local_id;
    if (!localId) {
      toast.error("No se encontró el ID del local.");
      console.error("Error: local_id no encontrado en localStorage.");
      setLoading(false);
      return;
    }
    const isGlobalCategory = Boolean(billsList.global_bill_category_id);
    const payload = {
      local_id: localId,
      local_bill_category_id: billsList.local_bill_category_id || null,
      global_bill_category_id: billsList.global_bill_category_id || null,
      global_bill_category_name: isGlobalCategory ? billsList.name : null,
      local_bill_category_name: isGlobalCategory ? null : billsList.name,
      amount: parseFloat(newBillAmount),
      bill_name: newBillName,
      bill_number: nuevoNum,
      bill_date: billDateToSend,
      file: files.length > 0 ? files[0].name : null,
    };
    console.log("Enviando payload al backend:", payload);
    const response = await createLocalBill(payload);
    if (response?.data) {
      handleAddBill(response.data);
      if (typeof fetchBills === 'function') {
        fetchBills();
      }
      toast.success("Factura creada con éxito.");
      setNewBillName('');
      setNewBillAmount('');
      setNewBillDate('');
      setNewBillNumber('');
      setFiles([]);
    } else {
      console.error("Respuesta inválida del backend:", response);
      toast.error("Error al crear la factura.");
    }
  } catch (error: any) {
    console.error("Error al agregar la factura:", error);
    toast.error(error.response?.data?.message || "Error al agregar la factura");
  } finally {
    setLoading(false);
  }
};

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(selectedFiles);
    }
  };

  const renderFilePreviews = () => {
    return files.map((file, index) => {
      const isImage = file.type.startsWith('image');
      return (
        <div key={index} className="file-preview">
          {isImage ? (
            <div className="file-preview-text">
              <i className="file-preview-icon">🖼️</i> {file.name}
            </div>
          ) : (
            <div className="file-preview-text">
              <i className="file-preview-icon">📄</i> {file.name}
            </div>
          )}
        </div>
      );
    });
  };

  useEffect(() => {
    if (!show) {
      setNewBillName('');
      setNewBillAmount('');
      setNewBillDate('');
      setNewBillNumber('');
      setFiles([]);
    }
  }, [show]);

  // Solo buscar por nombre o número de factura
  const filteredBills = billsList.bills
    ? billsList.bills.filter((bill: any) =>
        bill.bill_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bill.bill_number?.toString().includes(searchQuery)
      )
    : [];

  const paginatedBills = filteredBills.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleCalendar = () => {
    setShowCalendar((prev) => !prev);
  };


  const handleDeleteBill = async (billId: number) => {
    setBillIdToDelete(billId);
    setShowConfirmDelete(true);
  };

  const confirmDeleteBill = async () => {
    if (!billIdToDelete) return;
    try {
      await deleteLocalBillById(billIdToDelete);
      toast.success('Factura eliminada con éxito');
      if (setBillsList) {
        setBillsList((prev: BillType[]) => prev.filter((b: BillType) => b.bill_id !== billIdToDelete));
      }
    } catch (error) {
      toast.error('Error al eliminar la factura');
    } finally {
      setShowConfirmDelete(false);
      setBillIdToDelete(null);
    }
  };

  // Busca el objeto actualizado por bill_id antes de editar
  const handleEditBill = (bill: any) => {
    const allBills = billsList.bills || [];
    const foundBill = allBills.find((b: any) => b.bill_id === bill.bill_id) || bill;
    setBillToEdit(foundBill);
    setShowEditModal(true);
  };

  // Lógica para editar la categoría (similar a BillItem)
  const handleOpenEditCategoryModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEditCategoryModal(true);
  };
  const handleCloseEditCategoryModal = () => setShowEditCategoryModal(false);
  const handleEditCategory = (editedItem: { bill_item_id: number; item_name: string; item_description?: string; local_bill_category_id: number }) => {
    if (handleEditLocalCategory) {
      handleEditLocalCategory({
        local_bill_category_id: editedItem.local_bill_category_id,
        item_name: editedItem.item_name,
        item_description: editedItem.item_description,
      });
    } else {
      setBillsList((prev: BillType[]) =>
        prev.map(bill =>
          bill.bill_id === billsList.id ? { ...bill, name: editedItem.item_name, description: editedItem.item_description } : bill
        )
      );
    }
    setShowEditCategoryModal(false);
  };

  const handleSaveEditBill = (updatedBill: any) => {
    // Actualiza la lista localmente (puedes agregar lógica para guardar en backend si es necesario)
    setBillsList((prev: BillType[]) =>
      prev.map((b: BillType) =>
        b.bill_id === billToEdit.bill_id ? { ...b, ...updatedBill } : b
      )
    );
    setShowEditModal(false);
    setBillToEdit(null);
    toast.success('Factura editada con éxito');
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="bill-details-modal"
      className='modal-create-bill'
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <span>Detalles {billsList.name}  </span>
            {/* FaPen para editar la categoría */}
            <FaPen size={16} style={{ cursor: 'pointer', color:'var(--text-color)', marginLeft: '10px' }} onClick={handleOpenEditCategoryModal} />
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
                <p className="bill-details-description">
          {billsList.description}
          </p>
      <Modal.Body>
        <Form className="add-bill-form mb-4">
          <h5>Agregar Nuevo Pago</h5>
          <Form.Group className="mp-formgroup horizontal" controlId="billName">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nombre del pago"
              value={newBillName}
              onChange={(e) => setNewBillName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mp-formgroup horizontal" controlId="billAmount">
            <Form.Label>Monto</Form.Label>
            <Form.Control
              type="number"
              placeholder="Monto a pagar"
              value={newBillAmount}
              onChange={(e) => setNewBillAmount(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="dob" className="mp-formgroup horizontal fecha">
            <Form.Label>Fecha</Form.Label>
            <Button
              variant="no"
              onClick={toggleCalendar}
              className="boton-show-calendar"
            >
              {selectedDate
                ? selectedDate.toLocaleDateString()
                : ""} <FaCalendar />
            </Button>
          </Form.Group>
          <div className="calendar-container calendario-create-user">
            {showCalendar && (
              <div className='mp-form-calendar-container'>
                <Calendar
                  onChange={(date) => {
                    setSelectedDate(date as Date);
                    setShowCalendar(false);
                  }}
                  value={selectedDate}
                  locale="es-ES"
                  className="mp-form-calendar"
                />
              </div>
            )}
          </div>

          <Form.Group className="mp-formgroup horizontal" controlId="billNumber">
            <Form.Label>Número de Factura</Form.Label>
            <Form.Control
              type="number"
              placeholder="Número de factura"
              value={newBillNumber}
              onChange={(e) => setNewBillNumber(e.target.value)}
            />
          </Form.Group>

          {/*<FilesDisplayer/>*/}

            {/* <Form.Group className="mediopunto-multi-file-upload" controlId="billFiles">
            <Form.Label>Archivos</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
            />
            {renderFilePreviews()}
            </Form.Group> */}

          <Button
            variant="none"
            onClick={handleSubmit}
            className="form-button-create-bill-modal"
            disabled={loading}
          >
            {loading ? "Guardando..." : <><FaPlus /> Agregar Pago</>}
          </Button>
        </Form>

        <Form.Group controlId="searchBills" className="mb-3">
          <Form.Control
            className='search-bills-modal'
            type="text"
            placeholder="Buscar factura..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Form.Group>

<div className="scroll-container-table">
        <Table className="custom-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Fecha</th>
              <th>Monto</th>
              {isAdminOrSuperAdmin && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filteredBills.map((bill: any, index: number) => (
              <tr key={index}>
                <td>{bill.bill_number}</td>
                <td>{bill.bill_name}</td>
                <td>{formatDate(bill.bill_date)}</td>
                <td>{formatCurrency(bill.amount)}</td>
                {isAdminOrSuperAdmin && (
                  <td className='action-button-table-container'>
                    <button
                      className="item-detail-button"
                      onClick={() => handleEditBill(bill)}
                    >
                      <FaPen/>
                    </button>
                    <button className='item-delete-button' onClick={() => handleDeleteBill(bill.bill_id)}>
                      <FaTrash />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
        </div>

        <ModalConfirmDelete
          show={showConfirmDelete}
          onHide={() => setShowConfirmDelete(false)}
          onConfirm={confirmDeleteBill}
        />
        <ModalEditBill
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSave={handleSaveEditBill}
          initialData={billToEdit || { bill_name: '', amount: '', bill_date: '', bill_number: '', file: '' }}
        />
        {/* Modal para editar la categoría (ítem) */}
        <ModalEditBillItem
          show={showEditCategoryModal}
          handleClose={handleCloseEditCategoryModal}
          item={{
            bill_item_id: billsList.id,
            item_name: billsList.name,
            item_description: billsList.description || '',
            local_bill_category_id: billsList.local_bill_category_id,
          }}
          handleEditItem={handleEditCategory}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BillDetails;
